import axios, { AxiosInstance, AxiosError } from 'axios';
import { apiCache } from './cache';

const defaultBaseUrl =
  process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8000/api'
    : '/api';

// Create axios instance with base configuration
export const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || defaultBaseUrl, // Keep prod on same-origin, allow build overrides
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies/session data in requests
});

// Add request interceptor to include CSRF token if available
api.interceptors.request.use(
  (config) => {
    // Get CSRF token from meta tag if available
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    
    // For all POST/PUT/DELETE requests, ensure CSRF token is included
    if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
      // Get CSRF token from meta tag if available
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        // Fallback: get from cookies
        const cookies = document.cookie.split(';');
        const csrfCookie = cookies.find(cookie => cookie.trim().startsWith('csrftoken='));
        if (csrfCookie) {
          const token = csrfCookie.split('=')[1];
          config.headers['X-CSRFToken'] = token;
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling with improved error handling
api.interceptors.response.use(
  (response) => {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', response);
    }
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // Always log errors
    if (isDevelopment) {
      console.error('API Error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
    }
    
    // Handle different error status codes
    if (status === 401) {
      // 401 Unauthorized - user session expired
      // Clear any cached user data
      localStorage.removeItem('user');
      localStorage.removeItem('cart');
      localStorage.removeItem('country');
      localStorage.removeItem('currency');
      
      // Clear user-specific cache
      apiCache.clearUserCache();
      
      // Redirect to login page if not already there
      if (window.location.pathname !== '/accounts/login') {
        window.location.href = '/accounts/login';
      }
    } else if (status === 403) {
      // 403 Forbidden - user doesn't have permission
      // Clear user cache but don't redirect (may be intentional permission issue)
      apiCache.clearUserCache();
      // Let the error propagate so components can handle it appropriately
    } else if (status === 404) {
      // 404 Not Found - resource doesn't exist
      // Log but don't redirect, let component handle it
      if (isDevelopment) {
        console.warn('Resource not found:', error.config?.url);
      }
    } else if (status === 500) {
      // 500 Server Error - log detailed error
      if (isDevelopment) {
        console.error('Server error:', error.response?.data);
      }
    } else if (!error.response) {
      // Network error or no response
      if (isDevelopment) {
        console.error('Network error or no response from server');
      }
    }
    
    return Promise.reject(error);
  }
);

// Generic cached request function
export async function cachedRequest<T>(
  cacheKey: string,
  requestFn: () => Promise<T>,
  ttl?: number,
  forceRefresh: boolean = false
): Promise<T> {
  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = apiCache.get<T>(cacheKey);
    if (cached) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Cache hit for ${cacheKey}`);
      }
      return cached;
    }

    // Check if request is already pending
    const pending = apiCache.getPendingRequest<T>(cacheKey);
    if (pending) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Using pending request for ${cacheKey}`);
      }
      return pending;
    }
  }

  // Make new request
  if (process.env.NODE_ENV === 'development') {
    console.log(`Making new request for ${cacheKey}`);
  }
  const promise = requestFn();
  
  // Store as pending request
  apiCache.setPendingRequest(cacheKey, promise);

  try {
    const result = await promise;
    // Cache the result
    apiCache.set(cacheKey, result, ttl);
    // Clear pending request
    apiCache.clearPendingRequest(cacheKey);
    return result;
  } catch (error) {
    // Clear pending request on error
    apiCache.clearPendingRequest(cacheKey);
    throw error;
  }
}

